import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { customAxios } from "lib/axios";
import { refetchUserOnError } from "utils/refetchUserOnError";
import { showNotification } from "@mantine/notifications";
import { LeavesData } from "./getLeaveRequest";

export interface Leave {
  _id: string;
  leaveType: string;
  fromDate: string;
  toDate: string;
  reason: string;
  status: string;
}

export interface LeaveResponse {
  success: boolean;
  message: string;
  data: LeavesData;
}

type StatusPayload = {
  status: "approved" | "rejected";
};

const updateLeaveStatus = async ({
  leaveId,
  status,
  reviewedByReason,
}: {
  leaveId: string;
  status: StatusPayload["status"];
  reviewedByReason: string;
}): Promise<Leave> => {
  const { data } = await customAxios.patch<Leave>(`/leaves/status/${leaveId}`, {
    status,
    reviewedByReason,
  });
  return data;
};

export const useUpdateLeaveStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Leave,
    AxiosError,
    {
      leaveId: string;
      status: StatusPayload["status"];
      reviewedByReason: string;
    }
  >({
    mutationFn: updateLeaveStatus,

    onSuccess: async (updatedLeave, { leaveId, status }) => {
      queryClient.setQueryData<LeaveResponse | undefined>(["leaves"], (old) => {
        if (!old) return old;
        return {
          ...old,
          data: {
            pending: old.data.pending.map((l) =>
              l._id === leaveId ? { ...l, status } : l
            ),
            reviewed: old.data.reviewed,
          },

          // old.data.map((l) => (l._id === leaveId ? { ...l, status } : l)),
        };
      });

      await queryClient.invalidateQueries({
        queryKey: ["leaves"],
        exact: true,
      });

      showNotification({
        title: `Leave ${status === "approved" ? "Approved" : "Rejected"}`,
        message: `Leave request has been ${status}.`,
        color: status === "approved" ? "green" : "red",
      });
    },
    onError: (err) => {
      refetchUserOnError(err, queryClient);
    },
  });
};
